import { useState } from 'react';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { admin } from '@repletoryreach/sdk-api';
import { useCancelUpcomingRate } from '../../../../data/useSkuRates';
import { formatDate, formatMinor } from '../../../../lib/format';
import { chargeInForce } from '../../../../lib/priceLadder';
import { useT } from '../../../../i18n/I18nProvider';

type Sku = NonNullable<Awaited<ReturnType<typeof admin.listSkuRates>>['data']>[number];

/**
 * Cancel a rate dated ahead (an imported Meta card waiting for its quarter).
 * Nobody has been charged at it, so cancelling only means the rate in force
 * carries on past that day. The dialog says exactly that.
 */
export function CancelUpcomingButton({ rate }: { rate: Sku }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const mut = useCancelUpcomingRate();
  const date = formatDate(rate.effective_from);

  async function confirm() {
    try {
      await mut.mutateAsync(rate.id);
      notifySuccess(t('billing.skuRates.cancel.done', { sku: rate.sku_code, date }), t('billing.skuRates.cancel.doneTitle'));
      setOpen(false);
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.skuRates.cancel.failed'));
    }
  }

  return (
    <>
      <Button size="xs" variant="subtle" color="red" onClick={() => setOpen(true)}>{t('billing.skuRates.action.cancelUpcoming')}</Button>
      {open && (
        <Modal opened={open} onClose={() => setOpen(false)} title={t('billing.skuRates.cancel.title', { sku: rate.sku_code })} size="sm">
          <Stack gap="md">
            <Text role="bodySmall">
              {t('billing.skuRates.cancel.body', { date, charge: formatMinor(chargeInForce(rate), rate.currency) })}
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setOpen(false)}>{t('billing.skuRates.cancel.keep')}</Button>
              <Button color="red" loading={mut.isPending} onClick={() => void confirm()}>{t('billing.skuRates.cancel.confirm')}</Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </>
  );
}
